import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { api, ApiError, files } from '../lib/api';
import '../styles/templates.css';

const PREVIEW = Boolean(import.meta.env.VITE_PREVIEW);

/**
 * Muestra un PDF dentro de la aplicación, sin obligar a descargarlo.
 * Acepta una ruta de la API (se pide con la credencial) o una URL ya lista
 * (por ejemplo, un adjunto en base64).
 */
export function PdfViewer({
  title,
  apiPath,
  directUrl,
  filename,
  previewPath,
  onClose,
}: {
  title: string;
  apiPath?: string;
  directUrl?: string;
  filename?: string;
  /** En la vista previa no hay PDF real: se muestra la versión en pantalla. */
  previewPath?: string;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(directUrl ?? null);
  const [err, setErr] = useState<string | null>(null);

  const [previewLink, setPreviewLink] = useState<string | null>(null);

  useEffect(() => {
    if (!PREVIEW || !previewPath) return;
    api.post<{ url: string }>(previewPath, {})
      .then((r) => setPreviewLink(`${window.location.origin}/#${r.url}`))
      .catch(() => void 0);
  }, [previewPath]);

  useEffect(() => {
    if (directUrl || !apiPath) return;
    if (PREVIEW) { setErr('preview'); return; }
    let alive = true;
    let made: string | null = null;
    files.objectUrl(apiPath)
      .then((u) => { if (alive) { made = u; setUrl(u); } else { URL.revokeObjectURL(u); } })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'No se pudo abrir el documento.'));
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [apiPath, directUrl]);

  const download = async () => {
    if (directUrl) {
      const a = document.createElement('a');
      a.href = directUrl;
      a.download = filename ?? 'documento';
      a.click();
      return;
    }
    if (!apiPath) return;
    try { await files.download(apiPath, filename ?? 'documento.pdf'); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo descargar.'); }
  };

  const isImage = Boolean(directUrl && /^data:image\//.test(directUrl));

  return (
    <div className="pdfv-scrim" onMouseDown={onClose}>
      <div className="pdfv" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pdfv__head">
          <span className="pdfv__title">{title}</span>
          <div className="row gap-8 center">
            {!PREVIEW && url && !isImage && <button className="btn btn--ghost btn--sm" onClick={() => window.open(url, '_blank')}><Icon name="file" size={14} />Abrir en pestaña</button>}
            {!PREVIEW && <button className="btn btn--ghost btn--sm" onClick={download}><Icon name="download" size={14} />Descargar</button>}
            <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
        </div>
        <div className="pdfv__body">
          {err === 'preview' && (previewLink
            ? <iframe src={previewLink} title={title} />
            : (
              <div className="pdfv__state">
                This preview does not generate real PDF files.<br />
                In your local installation the document appears right here, without downloading.
              </div>
            ))}
          {err && err !== 'preview' && <div className="pdfv__state">{err}</div>}
          {!err && !url && <div className="pdfv__state">Preparando el documento…</div>}
          {!err && url && (isImage
            ? <img src={url} alt={title} />
            : (
              <object data={url} type="application/pdf" className="pdfv__object">
                <div className="pdfv__state">
                  Tu navegador no muestra el PDF aquí dentro.<br />
                  <button className="btn btn--accent" style={{ marginTop: 12 }} onClick={() => window.open(url, '_blank')}>
                    Abrir el PDF en una pestaña nueva
                  </button>
                </div>
              </object>
            ))}
        </div>
      </div>
    </div>
  );
}
